import { request } from './request';

/**
 * 资源域级 RBAC (2026-08-25/08-31, 后端 material-library internal/pkg/authz).
 *
 * 权限模型照 K8s: 角色 = 若干条 allow 规则 (resources × verbs, "*" 通配), 判定求并集,
 * 无匹配即拒绝。前端**不硬编码第二份权限表** —— 一次拉回生效规则本地判定,
 * 否则前后端两份规则必然漂移。
 *
 * 后端 RBAC_ENABLED 未开 = 老权限模型; 开了 = 按角色判定并拦截 (2026-09-24 起没有"判了不拦"的影子模式)。
 * 此时前端仍按规则渲染菜单, 但要允许"看不到入口却能直接访问"的过渡态存在。
 */

/** 资源域 (与后端 authz 常量同字面值). */
export type Resource =
    | 'material'
    | 'category'
    | 'dict'
    | 'dict_category'
    | 'cap_type'
    | 'device'
    | 'gallery'
    | 'tool'
    | 'phys_schema'
    | 'process_arch'
    | 'param_schema'
    | 'tip'
    | 'mm_group'
    | 'mm_mode'
    | 'mm_device_ref'
    | 'device_schema'
    | 'sku_schema'
    | 'schema_field'
    | 'param_row'
    | 'device_bundle'
    | 'material_bundle'
    | 'approval'
    | 'api_token'
    | 'admin_user'
    | 'datasync'
    | 'authz'
    | 'data_asset';

/** 动作. */
/**
 * 动作 (2026-09-01 收敛为四个): get=看(含列表/导出), create=新建,
 * update=编辑(含导入/排序/挂载/审批等治理动作), delete=删除.
 * 审批口径: 资源的写动作=发起变更(走审批); approval 的 update=批准/驳回;
 * 看自己提交的工单不需要 approval 权限 (后端按提交人过滤).
 */
export type Verb =
    | 'get'
    | 'create'
    | 'update'
    | 'delete'
    | 'publish'
    | 'approve';

/** 全部资源域 key (运行时判断用, 与 Resource 类型同步维护). */
export const RESOURCE_KEYS: Resource[] = [
    'material',
    'category',
    'dict',
    'dict_category',
    'cap_type',
    'device',
    'gallery',
    'tool',
    'phys_schema',
    'process_arch',
    'param_schema',
    'tip',
    'mm_group',
    'mm_mode',
    'mm_device_ref',
    'device_schema',
    'sku_schema',
    'schema_field',
    'param_row',
    'device_bundle',
    'material_bundle',
    'approval',
    'api_token',
    'admin_user',
    'datasync',
    'authz',
    'data_asset',
];

/**
 * 审批工单 target_type → RBAC 资源域 (与后端 parameter.approvalResource 同口径):
 * 参数行工单的 target_type 是工艺框架 code → param_row; 准入申请 access_user → admin_user; 其余同名.
 */
/** changereq 资源名 → RBAC 资源域别名 (与后端 authz.ResourceOf 同表): 不单独成域的工单类型归到所属域. */
export const RESOURCE_ALIAS: Record<string, Resource> = {
    access_user: 'admin_user',
    device_ext_value: 'device',
    device_material_compat: 'device',
};

/** 任意 changereq / batch 资源名 → RBAC 资源域 (别名归一; 其余同字面值). */
export function rbacResourceOf(name: string): Resource {
    return RESOURCE_ALIAS[name] ?? (name as Resource);
}

export function approvalResourceOf(targetType: string): Resource {
    if (RESOURCE_ALIAS[targetType]) return RESOURCE_ALIAS[targetType];
    if ((RESOURCE_KEYS as string[]).includes(targetType))
        return targetType as Resource;
    return 'param_row';
}

export interface Rule {
    resources: string[];
    verbs: string[];
}

export interface RoleInfo {
    name: string;
    displayName: string;
    description?: string;
    rules: Rule[];
    /** 系统角色 (admin / user): 由准入状态隐式生效, 规则可改、不可删. */
    system?: boolean;
    /** 额外绑定该角色的人数 (系统角色的隐式生效人数不计入). */
    bindingCount: number;
}

/** 词表项 (资源域/动作), 供角色编辑器渲染勾选矩阵. */
export interface VocabItem {
    key: string;
    label: string;
    group?: string;
    sensitive?: boolean;
    /** 读接口对所有已准入用户开放 (词表类): 角色编辑器把「无」档显示为「所有人可读」. */
    publicRead?: boolean;
    /** 额外说明: 写权不由本资源承载时说明它归谁 (角色编辑器在该行旁展示). */
    note?: string;
    /**
     * 该资源域**实际有路由使用**的动作 (后端从登记表推导)。
     * 例: manage 只有审批/令牌/人员/权限/同步五个资源用到, 在"材料"上给 manage
     * 等于授一个不存在的权限。编辑器只展示这里列出的动作。
     */
    verbs?: string[];
}

export interface Vocabulary {
    resources: VocabItem[];
    verbs: VocabItem[];
    wildcard: string;
}

export interface MyRoles {
    roles: string[];
    /** 角色名 → 显示名 (顶栏展示用, 随 my-roles 一起返回, 免调管理接口). */
    roleLabels?: Record<string, string>;
    /** 角色名 → 说明 (「我的权限」弹窗). */
    roleDescriptions?: Record<string, string>;
    rules: Rule[];
    /** 所在用户组及各组带来的角色 (2026-09-13; 角色已并入 roles, 这里只解释来源). */
    groups?: GroupGrant[];
    /** 后端是否启用了资源域级 RBAC; false = 跑在老权限模型下 (RBAC_ENABLED 未开). 开了就是真拦截, 没有影子模式 (2026-09-24). */
    rbacEnabled?: boolean;
}

/** 一个用户组给用户带来的角色. */
export interface GroupGrant {
    name: string;
    displayName: string;
    roles: string[];
}

export interface BindingRow {
    id: number;
    subjectKind: string;
    subjectId: string;
    roleName: string;
    namespace: string;
    createdAt: number;
    createdBy: number;
    /** 用户显示名 / 准入状态 (后端按 subject_id 补, 用户行不存在时为空) */
    displayName?: string;
    accessStatus?: string;
}

/** 当前用户的生效角色与规则 (免 RBAC 校验, 任意已准入用户可调). */
export function fetchMyRoles(): Promise<MyRoles> {
    return request<MyRoles>('/v1/authz/my-roles');
}

/** 单点自省 (kubectl auth can-i 同款); 常规渲染用 fetchMyRoles 本地判定即可. */
export function canI(
    resource: Resource,
    verb: Verb,
): Promise<{ allowed: boolean }> {
    return request('/v1/authz/can-i', {
        method: 'POST',
        body: { resource, verb },
    });
}

/** 角色清单 (admin). 角色全部来自 rbac_role 表, 代码里没有内置角色. */
export function listRoles(): Promise<{ list: RoleInfo[] }> {
    return request('/v1/authz/roles');
}

/** 资源域/动作词表 (admin), 角色编辑器渲染矩阵用. */
export function fetchVocabulary(): Promise<Vocabulary> {
    return request<Vocabulary>('/v1/authz/vocabulary');
}

/** 新建或覆盖自定义角色 (admin); 规则整体覆盖. */
export function saveRole(payload: {
    name: string;
    displayName: string;
    description?: string;
    rules: Rule[];
}): Promise<{ name: string }> {
    return request('/v1/authz/roles', { method: 'POST', body: payload });
}

/** 删除自定义角色 (admin); 仍被绑定引用时后端会拒绝. */
export function deleteRole(name: string): Promise<null> {
    return request('/v1/authz/roles/delete', {
        method: 'POST',
        body: { name },
    });
}

/** 绑定列表 (admin): email 模糊过滤 (邮箱 / 显示名), role 精确过滤 (角色成员面板). */
export function listBindings(
    email?: string,
    role?: string,
): Promise<{ list: BindingRow[] }> {
    return request('/v1/authz/bindings', { query: { email, role } });
}

/** 给用户绑定角色 (admin). */
export function createBinding(
    email: string,
    roleName: string,
): Promise<{ id: number }> {
    return request('/v1/authz/bindings', {
        method: 'POST',
        body: { email, roleName },
    });
}

/** 解除绑定 (admin). */
export function deleteBinding(id: number): Promise<null> {
    return request('/v1/authz/bindings/delete', {
        method: 'POST',
        body: { id },
    });
}

/** 系统角色 (admin / user): 绑定由准入审批 / 设为管理员维护, 权限面板只展示不增删. */
export const SYSTEM_ROLES = ['admin', 'user'];

/** 用户权限详情 (2026-09-11 用户权限面板): 准入状态 + 全部角色绑定 + 合并后的生效规则. */
export interface UserGrantDetail {
    email: string;
    displayName: string;
    /** none / pending / approved / rejected */
    accessStatus: string;
    /** 全部角色绑定, 含系统角色 admin / user (2026-09-12 起管理员也是显式绑定) */
    bindings: BindingRow[];
    roles: string[];
    roleLabels?: Record<string, string>;
    rules: Rule[];
    /** 所在用户组及各组角色 (面板只展示; 进出组在「用户组」tab 维护). */
    groups?: GroupGrant[];
}

/** 按邮箱取用户权限详情 (admin). */
export function fetchUserGrant(email: string): Promise<UserGrantDetail> {
    return request<UserGrantDetail>('/v1/authz/users/grant', {
        query: { email },
    });
}

/** 一次提交某用户的完整显式角色集合 (admin); 后端与现有绑定做差增删, 返回最新详情. */
export function setUserRoles(
    email: string,
    roles: string[],
): Promise<UserGrantDetail> {
    return request<UserGrantDetail>('/v1/authz/users/roles', {
        method: 'POST',
        body: { email, roles },
    });
}

/** 对任一资源域有该动作 (审批中心"看全站"的资格: 任一资源的 approve). */
export function allowsAny(rules: Rule[], verb: string): boolean {
    return rules.some((r) => r.verbs.includes('*') || r.verbs.includes(verb));
}

/** 规则并集判定 (与后端 authz.Allows 同语义: 任一规则同时命中资源与动作即放行). */
export function allows(rules: Rule[], resource: string, verb: string): boolean {
    return rules.some(
        (r) =>
            (r.resources || []).some((x) => x === '*' || x === resource) &&
            (r.verbs || []).some((x) => x === '*' || x === verb),
    );
}

// ---- 用户组 (2026-09-13) ----
// 组 = 一批用户 + 一组角色, 用户生效权限 = 直接绑定 ∪ 所在组角色 (并集). 系统角色 admin / user 不能挂到组.

export interface GroupInfo {
    id: number;
    name: string;
    displayName: string;
    description: string;
    /** 组绑定的角色名 */
    roles: string[];
    memberCount: number;
    createdAt: number;
    updatedAt: number;
}

export interface GroupMemberRow {
    id: number;
    groupName: string;
    email: string;
    createdAt: number;
    displayName?: string;
    accessStatus?: string;
}

/** 用户组清单 (admin): 含各组角色与成员数. */
export function listGroups(): Promise<{ list: GroupInfo[] }> {
    return request('/v1/authz/groups');
}

/** 新建或更新用户组 (admin): 显示名 / 说明 / 整套角色 (后端与现有组绑定做差增删). */
export function saveGroup(payload: {
    name: string;
    displayName: string;
    description?: string;
    roles: string[];
}): Promise<{ name: string }> {
    return request('/v1/authz/groups', { method: 'POST', body: payload });
}

/** 删除用户组 (admin): 连成员与组的角色绑定一起删, 成员本人的直接角色不受影响. */
export function deleteGroup(name: string): Promise<null> {
    return request('/v1/authz/groups/delete', {
        method: 'POST',
        body: { name },
    });
}

/** 组成员 (admin). */
export function listGroupMembers(
    group: string,
): Promise<{ list: GroupMemberRow[] }> {
    return request('/v1/authz/groups/members', { query: { group } });
}

/** 加成员 (admin): 须是平台用户. */
export function addGroupMember(group: string, email: string): Promise<null> {
    return request('/v1/authz/groups/members', {
        method: 'POST',
        body: { group, email },
    });
}

/** 移出成员 (admin). */
export function removeGroupMember(group: string, email: string): Promise<null> {
    return request('/v1/authz/groups/members/delete', {
        method: 'POST',
        body: { group, email },
    });
}

// ---- 权限变更审计 (2026-09-14) ----
// 角色 / 绑定 / 用户组 / 准入的每一次增改删留痕; before/after 是快照 JSON 文本, 只给人看.

export interface AuditRow {
    id: number;
    actorUid: number;
    actorEmail: string;
    actorName: string;
    /** create / update / delete */
    action: string;
    /** role / binding / group / group_member / user */
    targetKind: string;
    targetId: string;
    summary: string;
    before: string;
    after: string;
    createdAt: number;
}

/** 权限变更记录 (admin), 时间倒序. */
export function listAudits(params: {
    targetKind?: string;
    targetId?: string;
    actor?: string;
    action?: string;
    page?: number;
    pageSize?: number;
}): Promise<{ list: AuditRow[]; total: number }> {
    return request('/v1/authz/audits', { query: params });
}
